import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';

// Credit balance hook
export const useCreditBalance = () => {
    const token = useSelector(state => state.auth?.token);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchBalance = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        try {
            const res = await fetch(`${BACKEND_URL}/credit/balance`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setBalance(data.balance);
            }
        } catch (err) {
            // silently fail
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchBalance(); }, [fetchBalance]);
    return { balance, loading, refetch: fetchBalance };
};

// Get primary product image URL
export const getProductImageUrl = (product) => {
    if (!product) return '';
    if (product.photos && product.photos.length > 0) {
        const coverIdx = product.coverImageIndex || 0;
        return product.photos[coverIdx]?.url || product.photos[0]?.url || '';
    }
    if (product.generatedProductPhoto?.url) return product.generatedProductPhoto.url;
    if (product.photoUrls && product.photoUrls.length > 0) return product.photoUrls[0];
    return '';
};

// Person placeholder SVG matching SUSU Closet
export const PersonPlaceholderSVG = ({ size = 48 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48">
        <circle cx="24" cy="14" r="8" fill="none" stroke="#3bbaa8" strokeWidth="2" />
        <path d="M6 44 C6 32, 13 24, 24 24 C35 24, 42 32, 42 44" fill="none" stroke="#3bbaa8" strokeWidth="2" />
    </svg>
);

// Camera icon used in "Try another photo" buttons
const CameraIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </svg>
);

// Refresh icon used in "Redo" button
const RefreshIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
);

/**
 * Displays the VTO result image with WEARER / WEARING / LOOK bottom panel
 * and action buttons (Redo, Try another photo, Done).
 *
 * The image URL from the API is already watermarked or clean depending
 * on the user's license status -- no client-side watermark rendering needed.
 */
export const VTOResultPanel = ({
    result,
    personImageUrl,
    garmentImageUrl,
    onTryNewPhoto,
    onClose,
    cached = false,
    onRedo,
    vtoCost = 1,
    product,
    onPurchaseLicense,
    hasLicense = false
}) => {
    const isPaidLicense = product?.licensingEnabled && product?.licensingType === 'paid';
    const currency = product?.currency || 'USD';
    const formattedPrice = isPaidLicense && product?.licensingPrice
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(product.licensingPrice)
        : null;

    // If user already has the license, show clean image with Licensed badge
    const showLicensedBadge = isPaidLicense && hasLicense;
    // If user does NOT have license, the image is already watermarked from the backend
    const showPreviewBadge = isPaidLicense && !hasLicense;

    const [imageLoaded, setImageLoaded] = useState(false);
    const history = useHistory();

    const allImagesArray = result.allImages && result.allImages.length > 0 ? result.allImages : [result.imageUrl];
    const [selectedIndex, setSelectedIndex] = useState(allImagesArray.length - 1);

    useEffect(() => {
        const newArray = result.allImages && result.allImages.length > 0 ? result.allImages : [result.imageUrl];
        setSelectedIndex(newArray.length - 1);
    }, [result]);

    const handleLooksClick = () => {
        if (onClose) onClose();
        history.push('/closet?view=lookbook');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {/* Result images -- horizontally scrollable row */}
            <div className="looks-scroll-container" style={{ 
                display: 'flex', flexDirection: 'row', gap: '16px', overflowX: 'auto', width: '100%', 
                padding: '4px 4px 16px 4px', WebkitOverflowScrolling: 'touch',
                justifyContent: 'center'
            }}>
                <style>{`
                    .looks-scroll-container::-webkit-scrollbar { display: none; }
                    .looks-scroll-container { scrollbar-width: none; ms-overflow-style: none; }
                `}</style>
                
                <div style={{ 
                    flex: '0 0 auto', position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', 
                    borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                    // Only reserve space while loading. Once the image is in, the
                    // container hugs it so portrait results aren't letterboxed
                    // inside a wider grey box.
                    minWidth: imageLoaded ? 0 : '180px',
                    minHeight: imageLoaded ? 0 : '200px',
                    background: imageLoaded ? 'transparent' : '#f3f4f6'
                }}>
                    {!imageLoaded && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
                            <div style={{
                                width: '32px', height: '32px', border: '3px solid #e5e7eb',
                                borderTopColor: '#14b8a6', borderRadius: '50%',
                                animation: 'vtoSpin 1s linear infinite'
                            }} />
                            <span style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>Loading Preview...</span>
                        </div>
                    )}
                    <img
                        src={allImagesArray[selectedIndex]}
                        alt={`Try On Result ${selectedIndex + 1}`}
                        onLoad={() => setImageLoaded(true)}
                        style={{
                            // Bounded by both axes with objectFit: contain, so the
                            // original aspect ratio is preserved at whichever
                            // dimension binds first.
                            maxWidth: 'min(360px, 78vw)',
                            maxHeight: (isPaidLicense && !hasLicense) ? '46vh' : '56vh',
                            width: 'auto', height: 'auto',
                            objectFit: 'contain',
                            display: imageLoaded ? 'block' : 'none'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; setImageLoaded(true); }}
                    />

                    {/* Status badge -- Licensed */}
                    {showLicensedBadge && imageLoaded && (
                        <div style={{
                            position: 'absolute', top: '12px', right: '12px',
                            background: '#059669', color: 'white',
                            padding: '4px 10px', borderRadius: '999px',
                            fontSize: '11px', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: '4px',
                            boxShadow: '0 2px 8px rgba(5,150,105,0.3)'
                        }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Licensed
                        </div>
                    )}

                    {/* Status badge -- Preview */}
                    {showPreviewBadge && imageLoaded && (
                        <div style={{
                            position: 'absolute', top: '12px', right: '12px',
                            background: 'rgba(0,0,0,0.6)', color: 'white',
                            padding: '4px 10px', borderRadius: '999px',
                            fontSize: '11px', fontWeight: 600,
                            letterSpacing: '0.02em',
                            zIndex: 10
                        }}>
                            Preview
                        </div>
                    )}
                </div>
            </div>

            {isPaidLicense && !hasLicense && (
                /* Paid License Upgrade Card -- image is already watermarked from backend */
                <div style={{ width: '100%', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ padding: '10px 14px', background: 'linear-gradient(180deg, #F5FBF8 0%, #E1F5EE 100%)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: '#04342C', marginBottom: '2px' }}>Get the clean, full-resolution image</div>
                            <div style={{ fontSize: '12px', color: '#0F6E56', lineHeight: 1.4 }}>License this design -- no watermark, share or print anywhere.</div>
                        </div>
                        <button
                            onClick={() => onPurchaseLicense && onPurchaseLicense(product)}
                            style={{ background: '#1D9E75', border: 'none', color: 'white', fontWeight: 500, padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                            License {formattedPrice}
                        </button>
                    </div>
                    <div style={{ fontFamily: 'serif', fontSize: '11px', fontStyle: 'italic', color: '#9ca3af', lineHeight: 1.4, textAlign: 'center' }}>
                        Preview is for personal viewing. The watermarked image may not be removed or used commercially.
                    </div>
                </div>
            )}

            {/* Bottom panel - matching SUSU Closet layout */}
            <div style={{
                width: '100%', marginTop: '8px', padding: '10px 16px',
                background: '#ffffff', borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px'
            }}>
                {/* WEARER section */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', letterSpacing: '0.5px' }}>WEARER</div>
                    <div style={{
                        width: '48px', height: '60px', borderRadius: '8px', overflow: 'hidden',
                        border: '1.5px solid #14b8a6'
                    }}>
                        {personImageUrl && <img src={personImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                </div>

                {/* WEARING section */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', letterSpacing: '0.5px' }}>WEARING</div>
                    <div style={{
                        width: '72px', height: '60px', borderRadius: '8px', overflow: 'hidden',
                        border: garmentImageUrl ? '1.5px solid #e5e7eb' : '1.5px dashed #cbd5e1',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        background: garmentImageUrl ? 'transparent' : '#f8fafc'
                    }}>
                        {garmentImageUrl ? (
                            <img src={garmentImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <div style={{ fontSize: '9px', color: '#9ca3af', textAlign: 'center', lineHeight: 1.2 }}>
                                No items
                            </div>
                        )}
                    </div>
                </div>

                {/* LOOK section */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                    <div 
                        onClick={handleLooksClick}
                        style={{ fontSize: '10px', fontWeight: 600, color: '#14b8a6', marginBottom: '6px', letterSpacing: '0.5px', cursor: 'pointer' }}
                    >
                        LOOKS &gt;
                    </div>
                    <div style={{
                        display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px',
                        WebkitOverflowScrolling: 'touch', width: '100%', justifyContent: 'center'
                    }}>
                        {allImagesArray.filter(Boolean).map((imgUrl, idx) => (
                            <div key={idx} 
                                onClick={() => {
                                    setImageLoaded(false);
                                    setSelectedIndex(idx);
                                }}
                                style={{
                                width: '48px', height: '60px', borderRadius: '8px', overflow: 'hidden',
                                border: selectedIndex === idx ? '2px solid #14b8a6' : '1.5px solid #e5e7eb', flexShrink: 0,
                                cursor: 'pointer', opacity: selectedIndex === idx ? 1 : 0.6
                            }}>
                                <img src={imgUrl} alt={`Look ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', width: '100%' }}>
                {/* Redo button */}
                {onRedo && (
                    <button onClick={onRedo} title={`Redo (${vtoCost} Credit)`} style={{
                        flex: 1, padding: '8px',
                        background: '#14b8a6', color: 'white',
                        border: 'none',
                        borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                    }}>
                        <RefreshIcon />
                        Redo
                    </button>
                )}

                {/* Download button for free or licensed results */}
                <button onClick={async () => {
                    try {
                        const res = await fetch(allImagesArray[selectedIndex]);
                        const blob = await res.blob();
                        const blobUrl = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = `SHEYOU_TryOn_${Date.now()}.jpg`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(blobUrl);
                    } catch (e) {
                        window.open(allImagesArray[selectedIndex], '_blank');
                    }
                }} style={{
                    flex: 1, padding: '8px',
                    background: 'white', color: '#14b8a6',
                    border: '1.5px solid #14b8a6',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Save
                </button>

                {/* Share button for free or licensed results */}
                <button onClick={async () => {
                    if (navigator.share) {
                        try {
                            await navigator.share({
                                title: 'My SHEYOU Look',
                                text: 'Check out my new virtual try-on look from SHEYOU!',
                                url: allImagesArray[selectedIndex]
                            });
                        } catch (e) { console.error(e); }
                    } else {
                        navigator.clipboard.writeText(allImagesArray[selectedIndex]);
                        alert('Link copied to clipboard!');
                    }
                }} style={{
                    flex: 1, padding: '8px',
                    background: 'white', color: '#14b8a6',
                    border: '1.5px solid #14b8a6',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    Share
                </button>

                <button onClick={onClose} style={{
                    flex: 1, padding: '10px 8px',
                    background: 'white',
                    color: '#6b7280',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer'
                }}>Done</button>
            </div>
        </div>
    );
};

/**
 * Shows existing VTO results as a grid the user can tap to view,
 * plus a "Try with another photo" button.
 */
export const ExistingResultsGrid = ({
    results,
    vtoCost,
    onSelectResult,
    onTryNewPhoto,
    product,
    hasLicense = false
}) => {
    const isPaidLicense = product?.licensingEnabled && product?.licensingType === 'paid';
    const showPreviewBadge = isPaidLicense && !hasLicense;

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                    You've already tried on this item
                </p>
            </div>

            {/* Results grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: results.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: '12px', marginBottom: '16px',
                justifyItems: 'center'
            }}>
                {results.map(result => (
                    <div
                        key={result.id}
                        onClick={() => onSelectResult(result)}
                        style={{
                            position: 'relative',
                            borderRadius: '14px', overflow: 'hidden',
                            border: '2px solid #e5e7eb',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            aspectRatio: '3/4',
                            width: '100%',
                            maxWidth: results.length === 1 ? '320px' : 'none'
                        }}
                    >
                        <img
                            src={result.imageUrl || result.thumbnailUrl}
                            alt="Previous try-on"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />

                        {/* Status badge -- Preview (watermarked with SHEYOU x DESIGNER) */}
                        {showPreviewBadge && (
                            <div style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(0,0,0,0.6)', color: 'white',
                                padding: '3px 8px', borderRadius: '999px',
                                fontSize: '10px', fontWeight: 600,
                                letterSpacing: '0.02em',
                                zIndex: 10
                            }}>
                                Preview
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Try with another photo button */}
            <button
                onClick={onTryNewPhoto}
                style={{
                    width: '100%', padding: '14px',
                    background: 'white', color: '#14b8a6',
                    border: '1.5px solid #14b8a6',
                    borderRadius: '6px', fontSize: '15px', fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
            >
                <CameraIcon size={16} />
                Try with another photo - {vtoCost} Credit
            </button>
        </div>
    );
};
